import { Suspense, lazy } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PersonaDto } from '@/lib/api/personas-client';

import { ROUTES } from '@/shared/config/routes';
import { SectionLayout } from '../../components';
import { StartNewMenu, SuggestedAudienceAction } from './components';
import { useStartNewActions, useStartPersonaChat } from './lib';
import { useSaveAudienceDraft } from './lib/useSaveAudienceDraft';
import type { AudienceDraftCard } from './lib/audienceDrafts';

const PersonaDetailV2 = lazy(() =>
  import('@/features/audience-flow-widget').then((m) => ({ default: m.PersonaDetailV2 })),
);

interface IQSuggestedAudienceDetailProps {
  analysisId: string;
  card: AudienceDraftCard;
}

/**
 * One audience IQ suggested, in full.
 *
 * It reads from the report rather than from the Knowledge Hub, because until someone saves it
 * there is no persona to read. Everything the menu offers runs against that persona, so saving
 * is what turns the entries on and the menu says as much until then.
 */
export const IQSuggestedAudienceDetail = ({ analysisId, card }: IQSuggestedAudienceDetailProps) => {
  const { t } = useTranslation('analysis');
  const navigate = useNavigate();
  const save = useSaveAudienceDraft(analysisId);
  const startChat = useStartPersonaChat();
  const { onFocusGroup, onContentTest } = useStartNewActions(
    card.savedPersonaId ? [card.savedPersonaId] : [],
    analysisId,
  );

  const segmentName = card.persona?.segmentName ?? card.segment.name ?? '';
  const goBack = () => void navigate('..', { relative: 'path' });

  // Only the saved audience has a persona in the workspace, and a chat is with that persona -
  // which is why the menu offers nothing until then rather than saving on the way past.
  const handleChat = () => {
    if (!card.savedPersonaId) return;

    void startChat({
      personaId: card.savedPersonaId,
      segmentName,
    });
  };

  // There is no persona to render until the agents produce one, so the audience is shown as
  // the report described it, with why there is nothing more yet.
  if (!card.persona) {
    return (
      <SectionLayout
        variant="back"
        title={t('analysisResult.audienceProfiles.allAudienceProfiles')}
        onBack={goBack}
      >
        <div className="flex flex-col gap-3 p-5">
          <p className="text-sm text-foreground-alt">{card.segment.description}</p>
          <p className="text-sm text-muted-foreground">
            {card.status === 'failed'
              ? t('analysisResult.suggestedAudiences.thisAudienceCouldNot')
              : t('analysisResult.suggestedAudiences.thisAudienceIsStill')}
          </p>
        </div>
      </SectionLayout>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <SectionLayout
        variant="back"
        title={t('analysisResult.audienceProfiles.allAudienceProfiles')}
        onBack={goBack}
        actions={
          <StartNewMenu
            onChat={handleChat}
            chatUnavailableReason={
              card.savedPersonaId ? undefined : t('analysisResult.suggestedAudiences.saveToChat')
            }
            onFocusGroup={onFocusGroup}
            onContentTest={onContentTest}
          />
        }
      >
        <PersonaDetailV2
          persona={{ ...card.persona, personaTitle: segmentName } as PersonaDto}
          action={(segmentShare) => (
            <SuggestedAudienceAction
              segmentShare={segmentShare}
              saved={card.saved}
              isSaving={save.isPending}
              onSave={() =>
                save.mutate({ draftId: card.draftId, envelope: card.envelope, segmentName })
              }
              onView={() => void navigate(ROUTES.KNOWLEDGE_HUB_AUDIENCES)}
            />
          )}
        />
      </SectionLayout>
    </Suspense>
  );
};
